import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import { useNavigate } from "react-router";
import {
  useQpmMonitorCreateMutation,
  useQpmMonitorListQuery,
  useQpmMonitorServiceDefaultsListQuery,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { useEffect, useMemo, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/shared/components/ui/select";
import { MonitorIoTClassUtil } from "@/services/backend/qpm/lib/monitor-iot-class-util";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { resolveMonitorDefaultTimes } from "@/services/backend/qpm/monitors/lib/default-service-times";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import type { Monitor } from "@/services/backend/qpm/monitors/types/monitor";
import { MonitorOverviewTable } from "@/routes/base-data/processes/qpm/_components/monitor-overview-table";

export function BaseMonitorsTable({
  monitorTypeKey,
}: {
  monitorTypeKey: MonitorType;
}) {
  const navigate = useNavigate();
  const { companyId } = useActiveCompany();

  const {
    data: monitorList,
    isLoading,
    error,
  } = useQpmMonitorListQuery({
    company: { active: true, values: [companyId] },
    catalog: { active: false, values: [] },
    // list endpoint expects the same filter shape as catalog list
    type: { active: true, values: [monitorTypeKey] },
  });

  const [createMonitor, createState] = useQpmMonitorCreateMutation();
  const [newName, setNewName] = useState("");
  const [newIoTClass, setNewIoTClass] = useState<MonitorIoTClass | undefined>(
    undefined,
  );
  const [checkpointVisual, setCheckpointVisual] = useState(false);
  const [installNs, setInstallNs] = useState(0);
  const [maintainNs, setMaintainNs] = useState(0);

  const isIoT = monitorTypeKey === MonitorType.IoT;
  const isCheckpoint = monitorTypeKey === MonitorType.Checkpoint;
  const defaultProtocolValue = "LoRaWAN";
  const [protocolValue, setProtocolValue] = useState(defaultProtocolValue);
  const { data: serviceDefaults } = useQpmMonitorServiceDefaultsListQuery({});

  useEffect(() => {
    const defaults = resolveMonitorDefaultTimes(
      serviceDefaults,
      monitorTypeKey,
      isIoT ? newIoTClass : undefined,
      isCheckpoint && checkpointVisual,
    );
    setInstallNs(defaults.install);
    setMaintainNs(defaults.maintain);
  }, [
    serviceDefaults,
    monitorTypeKey,
    newIoTClass,
    isIoT,
    isCheckpoint,
    checkpointVisual,
  ]);

  useEffect(() => {
    // Ensure default is used whenever the dialog context changes to IoT.
    if (isIoT) setProtocolValue(defaultProtocolValue);
  }, [isIoT]);

  const canCreate =
    newName.trim().length > 0 &&
    !createState.isLoading &&
    (!isIoT || (!!newIoTClass && protocolValue.trim().length > 0));

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    if (isIoT && !newIoTClass) return;
    if (isIoT && protocolValue.trim().length === 0) return;

    const res = await createMonitor({
      companyId,
      name,
      // backend CreateReq expects string
      type: monitorTypeKey,
      installSeconds: Math.round(installNs / 1_000_000_000),
      maintainSeconds: Math.round(maintainNs / 1_000_000_000),
      checkpointVisual: isCheckpoint && checkpointVisual,
      ...(isIoT
        ? {
            iotClass: newIoTClass,
            iotProtocol: protocolValue.trim(),
          }
        : {}),
    }).unwrap();

    setNewName("");
    setNewIoTClass(undefined);
    setCheckpointVisual(false);
    setProtocolValue(defaultProtocolValue);
    const defaults = resolveMonitorDefaultTimes(
      serviceDefaults,
      monitorTypeKey,
      undefined,
      false,
    );
    setInstallNs(defaults.install);
    setMaintainNs(defaults.maintain);
    navigate(res.id);
  };

  const rows = useMemo(
    () =>
      monitorList?.items
        ? monitorList.items.map((monitor) => ({
            id: monitor.id,
            name: monitor.name ?? "",
            type: monitor.type,
            includeInQpmService: !!monitor.includeInCheckpointService,
            recommendedVisits: monitor.recommendedAmountOfMeasuresPerYear ?? 0,
            price: monitor.price ?? 0,
            updatedAt: monitor.updatedAt,
            rentingFees: monitorRentingFeesToRows(monitor),
            onEdit: () => navigate(monitor.id),
          }))
        : [],
    [monitorList?.items, navigate],
  );

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) return <MonitorsLoadingState />;

  const nameInputId = `new-monitor-name-${monitorTypeKey}`;
  const iotTypeId = `new-monitor-iot-type-${monitorTypeKey}`;
  const protocolId = `new-monitor-protocol-${monitorTypeKey}`;

  return (
    <div className="mt-3 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="default">
              <Plus />
              {MonitorTypeUtil.toLongString(monitorTypeKey)} {t("anlegen")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("Neuen ")}
                {MonitorTypeUtil.toLongString(monitorTypeKey)} {t("anlegen")}
              </DialogTitle>
              <DialogDescription>
                {t("Erstellen Sie hier einen neuen Basis-Monitor.")}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium" htmlFor={nameInputId}>
                {t("Name")}
              </Label>
              <Input
                id={nameInputId}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("Name")}
              />

              {isIoT ? (
                <>
                  <Label className="text-sm font-medium" htmlFor={iotTypeId}>
                    {t("IoT Typ")}
                  </Label>
                  <Select
                    value={newIoTClass}
                    onValueChange={(v) =>
                      setNewIoTClass(v as unknown as MonitorIoTClass)
                    }
                  >
                    <SelectTrigger id={iotTypeId}>
                      {newIoTClass
                        ? MonitorIoTClassUtil.toString(newIoTClass)
                        : t("Bitte auswählen")}
                    </SelectTrigger>
                    <SelectContent align="end">
                      {Object.values(MonitorIoTClass).map((v) => (
                        <SelectItem key={v} value={v}>
                          {MonitorIoTClassUtil.toString(v)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Label className="text-sm font-medium" htmlFor={protocolId}>
                    {t("Protokoll")}
                  </Label>
                  <Input
                    id={protocolId}
                    value={protocolValue}
                    onChange={(e) => setProtocolValue(e.target.value)}
                    placeholder={defaultProtocolValue}
                  />
                </>
              ) : null}

              {isCheckpoint ? (
                <div className="flex items-center gap-2 py-1">
                  <Checkbox
                    id={`new-monitor-checkpoint-visual-${monitorTypeKey}`}
                    checked={checkpointVisual}
                    onCheckedChange={(checked) =>
                      setCheckpointVisual(checked === true)
                    }
                  />
                  <Label
                    className="text-sm font-medium"
                    htmlFor={`new-monitor-checkpoint-visual-${monitorTypeKey}`}
                  >
                    {t("Visueller Prüfpunkt")}
                  </Label>
                </div>
              ) : null}

              <Label className="text-sm font-medium">
                {t("Installationszeit")}
              </Label>
              <TimeNumberInput value={installNs} onChange={setInstallNs} />

              <Label className="text-sm font-medium">{t("Servicezeit")}</Label>
              <TimeNumberInput value={maintainNs} onChange={setMaintainNs} />

              {createState.error ? (
                <RTKQueryErrorAlert error={createState.error} />
              ) : null}
            </div>

            <DialogFooter className="sm:justify-start">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  {t("Schließen")}
                </Button>
              </DialogClose>
              <Button
                type="button"
                onClick={submitCreate}
                disabled={!canCreate}
              >
                {t("Erstellen")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <MonitorOverviewTable rows={rows} monitorType={monitorTypeKey} />
    </div>
  );
}

function monitorRentingFeesToRows(monitor: Monitor) {
  if (monitor.type !== MonitorType.IoT) {
    return [];
  }
  const rentingFees = monitor.rentingFees as
    | {
        fees?: Record<
          string,
          { durationMonths?: number; price?: number; monthly?: number }
        >;
      }
    | Record<string, number | { durationMonths?: number; price?: number }>
    | undefined;

  const feeEntries =
    rentingFees && "fees" in rentingFees
      ? Object.entries(rentingFees.fees ?? {})
      : Object.entries(rentingFees ?? {});

  return feeEntries
    .map(([durationMonths, fee]) => {
      if (typeof fee === "number") {
        return {
          durationMonths: Number(durationMonths),
          monthly: fee,
        };
      }
      return {
        durationMonths: fee.durationMonths ?? Number(durationMonths),
        monthly: fee.price ?? fee.monthly ?? 0,
      };
    })
    .filter((fee) => fee.durationMonths > 0);
}

function MonitorsLoadingState() {
  return (
    <div className="mt-3 flex w-full flex-col gap-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
